export type ContentBlock =
  | { type: 'lead'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'quote'; text: string; cite?: string }
  | { type: 'numbered'; title: string; text: string }
  | { type: 'closing'; text: string }
  | { type: 'signature'; text: string };

export interface Article {
  slug: string;
  title: string;
  kicker: string;
  author: string;
  authorLine: string;
  date: string;
  dateLabel: string;
  category: string;
  excerpt: string;
  readTime: string;
  image: string;
  imageAlt: string;
  content: ContentBlock[];
}

export const articles: Article[] = [
  {
    slug: 'que-es-la-mentoria-estrategica',
    title: '¿Qué es la mentoría estratégica y por qué podría ser lo que necesitas?',
    kicker: 'Mentoría & Reflexión Estratégica',
    author: 'Erika López Castañeda',
    authorLine:
      'Por Erika López Castañeda — Fundadora del Método CALMA© Mentoría & Reflexión Estratégica',
    date: '2024-10-20',
    dateLabel: '20 oct 2024',
    category: 'Mentoría estratégica',
    excerpt:
      'Si ya construiste una base sólida y aun así sientes que algo no encaja, la mentoría estratégica puede ser el espejo que te falta: no para que alguien decida por ti, sino para que decidas con más claridad.',
    readTime: '6 min',
    image:
      'https://readdy.ai/api/search-image?query=Photorealistic%20editorial%20photo%2C%20top-left%20three-quarter%20view%20of%20a%20warm%20wooden%20desk%2C%20closed%20silver%20laptop%2C%20black%20eyeglasses%20resting%20on%20the%20laptop%2C%20white%20wireless%20mouse%2C%20empty%20white%20chair%20in%20soft-focus%20background%2C%20natural%20daylight%2C%20calm%20professional%20mood%2C%20warm%20taupe%20and%20cream%20tones%2C%20no%20text%2C%20no%20watermark&width=1200&height=900&seq=blog-card-mentoria-estrategica&orientation=landscape',
    imageAlt:
      'Escritorio de madera con laptop cerrada, lentes y mouse; espacio de trabajo sereno para reflexionar.',
    content: [
      {
        type: 'lead',
        text: 'Si llegaste a esta página, probablemente ya llevas un tiempo con una pregunta rondándote: ¿hay algo más para mí en esta etapa de mi carrera?',
      },
      {
        type: 'paragraph',
        text: 'Tal vez tienes un buen puesto, resultados sólidos y el respeto de tu equipo. Y aun así, algo no termina de encajar. Las decisiones se sienten más pesadas. El siguiente paso no está claro. O simplemente sientes que estás operando por debajo de tu potencial… y no sabes bien por qué.',
      },
      {
        type: 'paragraph',
        text: 'Ahí es exactamente donde entra la mentoría estratégica.',
      },
      { type: 'heading', text: '¿Por qué mentoría?' },
      {
        type: 'paragraph',
        text: 'La mentoría no es para líderes que están fallando. Es para líderes que ya construyeron una base sólida y que tienen el potencial y la disposición de ir más lejos.',
      },
      {
        type: 'paragraph',
        text: 'Elegir trabajar con un mentor es una de las inversiones más importantes que puedes hacer en tu vida profesional. No solo en tus habilidades o en tu siguiente puesto, sino en tu forma de liderarte a ti mismo o a ti misma antes de liderar a otros. Es un compromiso de tiempo y de recursos, sí. Pero el retorno —en claridad, en mejores decisiones, en satisfacción profesional— es exponencial.',
      },
      {
        type: 'paragraph',
        text: 'Hay algo que ningún curso, libro o podcast puede darte: un espejo. No podemos vernos la cara de forma directa; solo a través de un reflejo. Con nuestra propia conducta pasa algo similar: necesitamos la perspectiva de alguien externo para ver con claridad lo que nosotros mismos no podemos ver desde adentro. Un mentor te da ese reflejo: sin agenda, sin juicio y con experiencia real.',
      },
      {
        type: 'paragraph',
        text: 'Y en los momentos más difíciles de tu carrera —los cambios, las transiciones, las decisiones que no tienen una respuesta correcta— un acompañamiento estratégico marca la diferencia entre reaccionar desde el miedo y decidir desde la claridad.',
      },
      { type: 'heading', text: '¿Qué es la mentoría estratégica?' },
      {
        type: 'paragraph',
        text: 'La mentoría estratégica es una combinación de escucha profunda, preguntas de alto impacto, reflexión estructurada y experiencia real al servicio de tu desarrollo.',
      },
      {
        type: 'paragraph',
        text: 'Tu mentora no es solo alguien que te escucha. Es tu guía, tu espejo, tu caja de resonancia, tu voz crítica cuando la necesitas y también tu aliada cuando el camino se complica. Es alguien que ya recorrió el territorio que tú estás navegando, y que puede acompañarte sin decirte qué hacer, pero ayudándote a ver con más claridad para que tú decidas mejor.',
      },
      {
        type: 'paragraph',
        text: 'La mentoría incomoda. Y eso es exactamente lo que la hace funcionar. Como cualquier proceso de crecimiento real, implica mirar hacia adentro, cuestionarse y estar dispuesto o dispuesta a ver cosas que no siempre son cómodas. Si buscas validación, la mentoría no es para ti. Si buscas crecimiento real, bienvenida o bienvenido.',
      },
      { type: 'heading', text: '¿Cuándo la mentoría NO es la solución correcta?' },
      {
        type: 'paragraph',
        text: 'Ser honesta sobre esto es parte de mi trabajo. No todas las personas están en el momento adecuado para un proceso de mentoría, y empezarlo sin la disposición correcta es una pérdida de tiempo y de recursos para todos.',
      },
      {
        type: 'paragraph',
        text: 'Estos son los escenarios en los que la mentoría probablemente no sea lo que necesitas:',
      },
      {
        type: 'numbered',
        title: 'Cuando no reconoces que hay algo que trabajar.',
        text: 'Si crees que el problema siempre está afuera —en tu jefe, en tu empresa, en el mercado— y no estás dispuesto o dispuesta a voltear a verte a ti mismo o a ti misma, la mentoría no va a funcionar. El primer requisito es la disposición de mirarse con honestidad.',
      },
      {
        type: 'numbered',
        title: 'Cuando estás comprometido o comprometida con una dirección que no está alineada con tu organización.',
        text: 'Si ya decidiste que vas hacia un rumbo distinto al de tu empresa, un proceso de mentoría puede acelerar ese movimiento, pero no alinearte con algo que fundamentalmente rechazas. En ese caso, el trabajo es otro.',
      },
      {
        type: 'numbered',
        title: 'Cuando el problema real es que estás en el lugar equivocado.',
        text: 'A veces la incomodidad que sientes no tiene solución dentro del puesto o de la empresa actual. Si en el fondo sabes que necesitas un cambio de contexto, la mentoría puede ayudarte a verlo con más claridad, pero no a resignarte con algo que no es para ti.',
      },
      {
        type: 'numbered',
        title: 'Cuando buscas que alguien más tome tus decisiones por ti.',
        text: 'Un mentor no decide por ti. No te dice qué hacer. Te acompaña a encontrar tu propia claridad. Si lo que buscas es que alguien te diga exactamente qué pasos dar, este proceso no va a satisfacer esa expectativa.',
      },
      { type: 'heading', text: '¿Y cuándo SÍ es el momento?' },
      {
        type: 'paragraph',
        text: 'Cuando sientes que ya tienes experiencia, pero te falta perspectiva. Cuando estás en una transición importante —de puesto, de empresa, de etapa— y necesitas tomar decisiones con más claridad. Cuando sabes que tu estilo de liderazgo necesita evolucionar y quieres hacerlo de forma intencional. Cuando estás cansado o cansada de tomar decisiones desde la urgencia o desde lo que otros esperan de ti.',
      },
      {
        type: 'paragraph',
        text: 'La mentoría es para quienes quieren más: no en el sentido de más poder o más título, sino más autenticidad, más claridad y más congruencia entre lo que son y cómo lideran.',
      },
      {
        type: 'paragraph',
        text: 'Las mejores personas con las que he trabajado no llegaron a la mentoría porque algo estaba mal. Llegaron porque querían más de sí mismas. Y esa diferencia lo cambia todo.',
      },
      {
        type: 'closing',
        text: 'Si quieres explorar si este es tu momento, me da gusto tener una primera conversación contigo.',
      },
      {
        type: 'signature',
        text: '— Erika López Castañeda\nFundadora, CALMA© Mentoría & Reflexión Estratégica',
      },
    ],
  },
  {
    slug: 'lo-que-brene-brown-me-enseno-sobre-la-vulnerabilidad',
    title: 'Lo que Brené Brown me enseñó sobre la vulnerabilidad — y por qué lo veo en cada líder que acompaño',
    kicker: 'Liderazgo y vulnerabilidad',
    author: 'Erika López Castañeda',
    authorLine: 'Por Erika López Castañeda — CALMA© Mentoría & Reflexión Estratégica',
    date: '2024-07-10',
    dateLabel: '10 jul 2024',
    category: 'Liderazgo',
    excerpt:
      'La vulnerabilidad no es desmoronarse. Es el valor de mostrarse —con dudas, con preguntas— y el punto de partida de todo proceso de mentoría que de verdad transforma.',
    readTime: '7 min',
    image:
      'https://readdy.ai/api/search-image?query=Photorealistic%20overhead%20photo%20of%20a%20business%20meeting%2C%20hands%20pointing%20at%20a%20white%20tablet%20showing%20a%20rising%20line%20chart%2C%20printed%20reports%20on%20a%20light%20wood%20table%2C%20espresso%20cup%20with%20latte%20art%2C%20black%20smartphone%2C%20clipboard%2C%20people%20in%20white%20shirts%2C%20bright%20natural%20light%2C%20premium%20coaching%20aesthetic%2C%20warm%20neutral%20tones%2C%20no%20text%20overlay&width=1200&height=900&seq=blog-card-brene-brown&orientation=landscape',
    imageAlt:
      'Reunión de trabajo vista desde arriba: tablet con gráfica, café y documentos sobre la mesa.',
    content: [
      {
        type: 'lead',
        text: 'Hay conversaciones que te cambian. No de forma dramática ni de un día para otro, sino de esas que se quedan rondándote y te hacen ver tu trabajo —y a las personas que acompañas— con ojos distintos.',
      },
      {
        type: 'paragraph',
        text: 'La primera vez que escuché a Brené Brown hablar sobre la vulnerabilidad fue una de esas conversaciones. Y desde entonces no puedo dejar de verla en cada sesión que tengo con un líder.',
      },
      { type: 'heading', text: 'Lo que Brown descubrió — y lo que me resonó profundamente' },
      {
        type: 'paragraph',
        text: 'Brené Brown es investigadora. Pasó años estudiando la conexión humana: qué nos une, qué nos separa y qué hace que algunas personas vivan con más plenitud que otras. Y encontró algo que, al principio, no esperaba encontrar: la vulnerabilidad.',
      },
      {
        type: 'paragraph',
        text: 'Lo que ella descubrió es que las personas que experimentan más conexión genuina, más sentido de pertenencia y más plenitud en sus vidas tienen algo en común. No son las más fuertes, ni las más seguras, ni las que nunca dudan. Son las que se permiten ser vistas. Las que tienen el valor de mostrarse tal como son, con sus miedos, sus dudas y sus preguntas sin resolver.',
      },
      {
        type: 'quote',
        text: 'La vulnerabilidad es la cuna de la innovación, la creatividad y el cambio.',
        cite: 'Brené Brown',
      },
      {
        type: 'paragraph',
        text: 'Hay una frase suya que no puedo dejar de pensar cuando estoy frente a un líder que llega a sesión con la guardia alta. Si eso es cierto —y en mi experiencia lo es—, entonces el liderazgo blindado, el de quien nunca admite que no sabe, el de quien siempre tiene la respuesta lista, no solo cuesta caro en lo personal. También cuesta caro en lo profesional.',
      },
      { type: 'heading', text: 'Por qué los líderes le temen a la vulnerabilidad' },
      {
        type: 'paragraph',
        text: 'En mis años acompañando líderes he visto este patrón más veces de las que puedo contar: alguien llega a una posición de liderazgo con mucho talento, mucho esfuerzo y mucha entrega. Y en el camino aprende —de forma explícita o implícita— que mostrarse inseguro es peligroso. Que admitir que no sabes algo es una debilidad. Que pedir ayuda puede interpretarse como incompetencia.',
      },
      {
        type: 'paragraph',
        text: 'Así que construyen una armadura. Y esa armadura, que en algún momento los protegió, se convierte con el tiempo en lo que los limita.',
      },
      {
        type: 'paragraph',
        text: 'Brown lo dice con claridad: cuando bloqueamos las emociones difíciles —la incertidumbre, el miedo, la duda— también bloqueamos las que nos conectan con los demás y con nosotros mismos. No podemos seleccionar qué sentimos. Es todo o nada.',
      },
      {
        type: 'paragraph',
        text: 'Lo que veo en sesión es exactamente eso. El líder que nunca muestra dudas tampoco genera confianza real en su equipo. Quien nunca pide perspectiva externa tampoco aprende de quienes lo rodean. Quien siempre tiene la respuesta correcta deja de hacer las preguntas correctas.',
      },
      { type: 'heading', text: 'La vulnerabilidad como punto de partida — no como destino' },
      {
        type: 'paragraph',
        text: 'Aquí es donde quiero ser muy precisa, porque creo que la vulnerabilidad se malentiende con frecuencia.',
      },
      {
        type: 'paragraph',
        text: 'Ser vulnerable no es desmoronarse. No es compartir todo con todos. No es perder el piso.',
      },
      {
        type: 'paragraph',
        text: 'Es reconocer que no lo tienes todo resuelto. Es admitir —primero ante ti mismo o ante ti misma— que hay áreas donde podrías crecer, decisiones que podrían tomarse mejor y patrones que ya no te sirven. Es tener la honestidad de decir: «No sé todo lo que necesito saber para llegar a donde quiero llegar».',
      },
      {
        type: 'paragraph',
        text: 'Ese reconocimiento —ese momento de honestidad interna— es, en mi experiencia, el primer paso real hacia la transformación. Y es exactamente lo que separa a quienes buscan un proceso de mentoría de quienes no.',
      },
      {
        type: 'heading',
        text: 'Lo que nos enseñaron sobre mostrar dudas — y por qué ya no funciona',
      },
      {
        type: 'paragraph',
        text: 'He acompañado a personas brillantes, con carreras impresionantes y resultados extraordinarios. Y quienes más avanzan en un proceso de mentoría no son necesariamente los más talentosos. Son los más curiosos.',
      },
      {
        type: 'paragraph',
        text: 'Son quienes llegan a sesión con preguntas, no solo con respuestas. Quienes, cuando les señalo algo difícil de ver, se detienen a considerarlo en lugar de defenderse. Quienes reconocen que no son las personas más listas de la sala… y que precisamente por eso quieren seguir aprendiendo.',
      },
      {
        type: 'paragraph',
        text: 'Brown habla de esto como la disposición a ser visto o vista. Yo lo veo en sesión como la disposición a cuestionarse: a hacerse preguntas incómodas y a mirar más profundo que la superficie.',
      },
      {
        type: 'paragraph',
        text: 'Esas personas no buscan un mentor porque algo está mal. Lo buscan porque entienden que el crecimiento real requiere perspectiva externa y tienen la humildad de pedirla.',
      },
      { type: 'heading', text: 'Lo que me llevo de Brené Brown a mi trabajo en CALMA' },
      {
        type: 'paragraph',
        text: 'Cuando acompaño a un líder, una de las primeras cosas que busco entender es cuánto espacio se da para ser vulnerable —consigo mismo o consigo misma, al menos—. No en público. No con todo el mundo. Consigo mismo o consigo misma.',
      },
      {
        type: 'paragraph',
        text: '¿Puede decirse que algo no está funcionando? ¿Puede reconocer que hay un patrón que se repite? ¿Puede sentarse con la incomodidad de no saber qué sigue, sin apresurarse a construir una respuesta que lo calme?',
      },
      {
        type: 'paragraph',
        text: 'Esa disposición no se puede fingir. Y cuando está presente, el trabajo de mentoría fluye de una forma completamente distinta. Cuando no está, el proceso se convierte en una conversación muy inteligente que no mueve nada.',
      },
      {
        type: 'paragraph',
        text: 'Por eso, cuando alguien me pregunta qué necesita para sacarle el mayor provecho a un proceso de mentoría, siempre pienso en lo que Brown encontró en su investigación: el valor de mostrarse. No de tenerlo todo resuelto. No de llegar con las respuestas correctas.',
      },
      {
        type: 'paragraph',
        text: 'Solo el valor de llegar siendo quien eres —con tus dudas, tu incertidumbre y tus preguntas— y confiar en que, desde ahí, juntos podemos encontrar más claridad.',
      },
      {
        type: 'paragraph',
        text: 'Ese es el punto de partida. Y, en mi experiencia, también es el más transformador.',
      },
      {
        type: 'closing',
        text: 'Si algo de lo que leíste aquí resuena contigo, me da gusto tener una primera conversación.',
      },
      {
        type: 'signature',
        text: '— Erika López Castañeda\nFundadora, CALMA© Mentoría & Reflexión Estratégica',
      },
    ],
  },
];